import { notification } from 'antd'
import enUs from 'antd/es/locale/en_US'
import itIt from 'antd/es/locale/it_IT'
import esEs from 'antd/es/locale/es_ES'
import frFr from 'antd/es/locale/fr_FR'

import { TranslationKeys } from '../Constants/TranslationKeys'
import {
  LocalhostUrl,
  TableColumnTranslationPrefix,
  Total,
  UnretrievedTranslation,
  Purchased,
  Delivered,
  Env,
  Language_EN,
  Language_ES,
  Language_FR,
} from '../Constants/Configuration'
import {
  ArticleColumn,
  ArticleCodeColLength,
  ArticleDefaultColLength,
  BarcodeColLength,
  BrandColLength,
  BrandSuperColLength,
  CategoryColLength,
  SuperCategoryColLength,
  CollectionTypeColLength,
  ColorColLength,
  CountryOriginColLength,
  CustomsCodeColLength,
  DefaultFreeWidthMaxLength,
  DepartmentColLength,
  DetailedCompositionColLength,
  DiscountPrice1ColLength,
  DiscountPrice2ColLength,
  DiscountPriceColLength,
  ExtraArticleDescriptionColLength,
  ExtraDescriptionSpecialColLength,
  FabricColLength,
  FarfetchIdColLength,
  GrossWholesalePriceColLength,
  LifestyleColLength,
  OriginalRetailPriceColLength,
  PatternColLength,
  ProductDescriptionColLength,
  RetailPriceColLength,
  SeasonColLength,
  SizeAndFitColLength,
  StorageLocationColLength,
  SubCategoryColLength,
  SuperColorColLength,
  SupplierColLength,
  WholesalePriceColLength,
  ExtraShortDescriptionColLength,
  defaultCurrency,
  ArticleDefaultColumn,
} from '../Constants/TableConfiguration'
import { formatString, setCapitalLetter } from './StringHelpers'
import { saveNewLineColumns } from '../Slices/Kiosk/TableOptionsSlice'
import { WorkSpaces } from '../Constants/WorkSpace'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { getChannels } from 'Services/Omnichannel/ChannelService'
import { getSitePricelists } from 'Services/Settings/SiteSettingsService'
import { getStores } from 'Services/Omnichannel/StoresService'

declare global {
  interface Window {
    chrome: any
    reusableCanvas: any
  }
}

const hasSizeEnabled = function (filters) {
  if (filters && filters.predefinedFilters && filters.predefinedFilters.length) {
    return !!filters.predefinedFilters.find(element => element.value === ArticleColumn.Size && element.active)
  }
  return false
}

const getParameterByName = (name, url = window.location.href) => {
  name = name.replace(/[[\]]/g, '\\$&')
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url)
  if (!results) return null
  if (!results[2]) return ''
  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

const renderTranslation = (language, key) => {
  if (language && !isEmptyObject(language)) {
    if (language[key]) {
      return language[key]
    }
  } else {
    return ''
  }

  if (!key) {
    return UnretrievedTranslation
  }

  return key
}

const renderSelectionCountHtml = (selectionLength, isFullSelection, language) => {
  const selectAllBox = document.querySelector('#article-table .ant-table-selection')
  if (selectAllBox) {
    const counterText = isFullSelection
      ? renderTranslation(language, TranslationKeys.editResultFullSelect)
      : '(' + selectionLength + ')'
    const selectCounter = document.querySelector('.row-select-counter')
    if (!selectCounter) {
      let element = document.createElement('div')
      element.innerHTML = counterText
      element.classList.add('row-select-counter')
      selectAllBox.appendChild(element)
    } else {
      selectCounter.innerHTML = counterText
    }
  }
}

const getCurrencyConversion = value => {
  if (value === undefined) return '0'
  const formattedValue = new Intl.NumberFormat('it-IT').format(value)
  return formattedValue
}

const getCurrencySymbolByPricelist = (pricelist, pricelistName) => {
  const activePriceList = pricelist.find(p => p.name === pricelistName)
  return activePriceList ? activePriceList.currencySymbol : defaultCurrency
}

const handleErrors = response => {
  if (!response.ok) {
    return response.text().then(text => {
      throw new Error(text ?? response.statusText)
    })
  }

  return response
}

const handleErrorCatchLog = (err, customMessage) => {
  console.log(err)
  console.log(customMessage)

  if (isDemoSite() || isLocalhost()) {
    notification.error({
      message: customMessage,
      description: err && err.message ? err.message : err && err.stack ? err.stack : '',
      placement: 'topRight',
      duration: 5,
    })
  }
}

const createRequestOptions = (method, body, includeCredentials, enableCors, stringify: boolean) => {
  return {
    method: method,
    headers: stringify ? { 'Content-Type': 'application/json' } : {},
    body: body ? (stringify ? JSON.stringify(body) : body) : undefined,
    credentials: (includeCredentials ? 'include' : undefined) as RequestCredentials,
    mode: (enableCors ? 'cors' : undefined) as RequestMode,
    cache: 'no-cache' as RequestCache,
  }
}

const isLocalhost = () => {
  return window.location.href.indexOf('127.0.0.1') !== -1 || window.location.href.indexOf('localhost') !== -1
}

const isDevelopment = () => {
  return (
    (!!process.env.NODE_ENV && process.env.NODE_ENV === Env.Develop) ||
    (!!process.env.REACT_APP_ENV && process.env.REACT_APP_ENV === Env.Develop)
  )
}

const isDemoSite = () => {
  return !!process.env.REACT_APP_ENV && process.env.REACT_APP_ENV === Env.Demo
}

const isProdSite = () => {
  return false
}

const getBaseUrl = () => {
  if (isLocalhost() && isDevelopment()) {
    return !!process.env.REACT_APP_LOCALHOST ? process.env.REACT_APP_LOCALHOST : LocalhostUrl
  } else {
    return ''
  }
}

const createRequest = (
  url: string,
  method: string,
  body: {},
  token: string,
  includeCredentials = false,
  enableCors = false,
) => {
  let requestOptions = createRequestOptions(method, body, includeCredentials, enableCors, true)
  if (token) requestOptions.headers['Authorization'] = `Bearer ${token}`
  return fetch(getBaseUrl() + url, requestOptions).then(handleErrors)
}

const createRequestNoStringifyBody = (
  url: string,
  method: string,
  body: {},
  token: string,
  includeCredentials = false,
  enableCors = false,
) => {
  let requestOptions = createRequestOptions(method, body, includeCredentials, enableCors, false)
  if (token) requestOptions.headers['Authorization'] = `Bearer ${token}`
  return fetch(getBaseUrl() + url, requestOptions).then(handleErrors)
}

const retrieveMktAliasOrName = (mktColumnName, mktAlias) => {
  if (mktAlias && mktAlias.length !== 0) {
    const mktResult = mktAlias.find(marketplace => marketplace.column === mktColumnName)
    if (mktResult) {
      return mktResult.alias ? mktResult.alias : mktResult.name
    }
  }

  return mktColumnName
}

const isFromIntegration = window => {
  return window && window.chrome && window.chrome.webview
}

const retrieveSelloutAvarageWeighted = (checkedStores, sizeId) => {
  const purchased = []
  const delivered = []

  if (checkedStores) {
    retrieveSizeValuesFromCheckedStore(purchased, delivered, checkedStores)
  }

  const purchasedTotal = sizeId
    ? purchased
        .filter(x => x.sizeId === sizeId)
        .map(x => x.quantity)
        .reduce((prev, curr) => curr + prev, null)
    : purchased
        .filter(x => x.name === Total)
        .map(x => x.quantity)
        .reduce((prev, curr) => curr + prev, null)
  const deliveredTotal = sizeId
    ? delivered
        .filter(x => x.sizeId === sizeId)
        .map(x => x.quantity)
        .reduce((prev, curr) => curr + prev, null)
    : delivered
        .filter(x => x.name === Total)
        .map(x => x.quantity)
        .reduce((prev, curr) => curr + prev, null)

  return Math.round((purchasedTotal * 100) / deliveredTotal)
}

const stopPropagation = e => {
  e.stopPropagation()
}

const preventDefault = e => {
  e.preventDefault()
}

const handleRequest = (getRequest, rowKey, reduxAction, errorMessage, dispatch, callback) => {
  return () => {
    getRequest(rowKey)
      .then(data => {
        dispatch(reduxAction(data))
      })
      .catch(e => {
        console.log(errorMessage)
      })
      .finally(() => {
        if (callback) {
          callback()
        }
      })
  }
}

const retrieveColumnWidth = colName => {
  switch (colName) {
    case ArticleColumn.ArticleCode:
      return ArticleCodeColLength
    case ArticleColumn.FarfetchId:
      return FarfetchIdColLength
    case ArticleColumn.Season:
      return SeasonColLength
    case ArticleColumn.CollectionType:
      return CollectionTypeColLength
    case ArticleColumn.Supplier:
      return SupplierColLength
    case ArticleColumn.SuperBrand:
      return BrandSuperColLength
    case ArticleColumn.Brand:
      return BrandColLength
    case ArticleColumn.Category:
      return CategoryColLength
    case ArticleColumn.SuperCategory:
      return SuperCategoryColLength
    case ArticleColumn.SubCategory:
      return SubCategoryColLength
    case ArticleColumn.Department:
      return DepartmentColLength
    case ArticleColumn.ProductDescription:
      return ProductDescriptionColLength
    case ArticleColumn.SuperColor:
      return SuperColorColLength
    case ArticleColumn.Color:
      return ColorColLength
    case ArticleColumn.Fabric:
      return FabricColLength
    case ArticleColumn.Pattern:
      return PatternColLength
    case ArticleColumn.Lifestyle:
      return LifestyleColLength
    case ArticleColumn.StorageLocation:
      return StorageLocationColLength
    case ArticleColumn.Barcode:
      return BarcodeColLength
    case ArticleColumn.CountryOrigin:
      return CountryOriginColLength
    case ArticleColumn.CustomsCode:
      return CustomsCodeColLength
    case ArticleColumn.DetailedComposition:
      return DetailedCompositionColLength
    case ArticleColumn.ShortDescription:
      return ExtraShortDescriptionColLength
    case ArticleColumn.Description:
      return ExtraArticleDescriptionColLength
    case ArticleColumn.SpecialDescription:
      return ExtraDescriptionSpecialColLength
    case ArticleColumn.SizeAndFit:
      return SizeAndFitColLength
    case ArticleColumn.OriginalRetailPrice:
      return OriginalRetailPriceColLength
    case ArticleColumn.RetailPrice:
      return RetailPriceColLength
    case ArticleColumn.DiscountedPrice:
      return DiscountPriceColLength
    case ArticleColumn.DiscountedPrice1:
      return DiscountPrice1ColLength
    case ArticleColumn.DiscountedPrice2:
      return DiscountPrice2ColLength
    case ArticleColumn.GrossWholesalePrice:
      return GrossWholesalePriceColLength
    case ArticleColumn.WholesalePrice:
      return WholesalePriceColLength
    default:
      return ArticleDefaultColLength
  }
}

const calculateColumnFreeWidth = (
  colName,
  isPriceCol,
  isLanguageCol,
  isMarketplace,
  language,
  defaultCols,
  resultArray,
  dispatch,
) => {
  if (!isPriceCol && !isMarketplace && !isLanguageCol) {
    const columnResults = resultArray.map(result => {
      return result[colName] ? result[colName] : ''
    })

    const maxHeadColName = formatString(
      renderTranslation(language, TranslationKeys.tableEmptyColumnsAutocomplete),
      renderTranslation(language, TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(colName)]),
    )

    if (columnResults && columnResults.length !== 0) {
      const maxResult = columnResults.reduce((a, b) => {
        return a.length > b.length ? a : b
      }, '')
      const textToAdd = maxHeadColName.length > maxResult.length ? maxHeadColName : maxResult

      const newColPixels = calculateColumnWidthBasedOnText(textToAdd)

      if (newColPixels < DefaultFreeWidthMaxLength) {
        return newColPixels
      } else {
        if (retrieveRenderNewLineColumn(colName, defaultCols)) {
          dispatch(saveNewLineColumns(colName))
        }

        return DefaultFreeWidthMaxLength
      }
    }
  }

  return retrieveColumnWidth(colName)
}

const retrieveRenderNewLineColumn = (colName: string, defaultCols: ArticleDefaultColumn[]) => {
  const isPriceColumn = defaultCols.find(item => item.key && item.key === colName && item.price)
  const isMarketplaceColumn = defaultCols.find(item => item.name && item.name === colName && item.isMarketplace)
  return (
    colName !== ArticleColumn.ArticleCode &&
    !isPriceColumn &&
    colName !== ArticleColumn.ProductDescription &&
    !isMarketplaceColumn
  )
}

const isEmptyObject = obj => {
  return obj && Object.keys(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype
}

const fixWorkSpaceCase = workspaceName => {
  const workspaceNames = Object.keys(WorkSpaces)
  return workspaceNames.find(name => name.toUpperCase() === workspaceName.toUpperCase())
}

const isFixedWorkspace = workspaceName => {
  const workspaceNames = Object.keys(WorkSpaces)
  return !!workspaceNames.find(name => name.toUpperCase() === workspaceName.toUpperCase())
}

const handleMultipleLeaveTableHover = e => {
  const rowsMultipleTables = document.querySelectorAll('[class*="sameRow-"]')
  rowsMultipleTables &&
    rowsMultipleTables.forEach(row => {
      row.querySelector('.listing-price')?.classList.remove('ant-table-cell-row-hover')
      row.querySelector('.listing-discount')?.classList.remove('ant-table-cell-row-hover')
      row.querySelector('.listing-start-date')?.classList.remove('ant-table-cell-row-hover')
    })
}
const handleMultipleTableHover = e => {
  if (e.currentTarget) {
    const classSameRowWithIndex = e.currentTarget.className.split(' ').filter((e: string) => e.startsWith('sameRow'))
    const rowsMultipleTablesToSelect = document.querySelectorAll('.' + classSameRowWithIndex)
    rowsMultipleTablesToSelect &&
      rowsMultipleTablesToSelect.forEach(row => {
        row.querySelector('.listing-price')?.classList.add('ant-table-cell-row-hover')
        row.querySelector('.listing-discount')?.classList.add('ant-table-cell-row-hover')
        row.querySelector('.listing-start-date')?.classList.add('ant-table-cell-row-hover')
      })
  }
}

const handleMultipleTableScroll = e => {
  const scrollableTables = document.querySelectorAll('.same-table-scroll .ant-table-body')
  scrollableTables &&
    scrollableTables.forEach(table => {
      if (table && e.target && table !== e.target) {
        table.scrollTop = e.target.scrollTop
      }
    })
}

const handleVirtualScroll = (target, scrollTop) => {
  const scrollableTables = document.querySelectorAll('.same-table-scroll .virtual-list')
  const brandTable = document.querySelector('#discount-main-table .ant-table-body')

  if (brandTable && target && brandTable !== target) {
    brandTable.scrollTop = scrollTop
  }

  scrollableTables &&
    scrollableTables.forEach(table => {
      if (table && target && table !== target) {
        table.scrollTop = scrollTop
      }
    })
}

const handleMultipleTableVirtualScroll = e => {
  handleVirtualScroll(e.target, e.target.scrollTop)
}

const handleMultipleTableVirtualScrollX = (scrollX, tableRef, componentWidth, setIsVirtualized) => {
  if (tableRef && tableRef.current) {
    const maxElementwidth = tableRef.current.offsetLeft + componentWidth
    const brandTable = document.getElementById('discount-main-table')
    const brandTableWidth = brandTable.clientWidth + 4
    const scrollingTable = document.getElementById('discount-tables-container')
    const discountScrollingWidth = scrollingTable.clientWidth
    if (scrollX + discountScrollingWidth < tableRef.current.offsetLeft || scrollX + brandTableWidth > maxElementwidth) {
      setIsVirtualized(true)
    } else {
      setIsVirtualized(false)
    }
  }
}

const calculateColumnWidthBasedOnText = (text, offset = 30) => {
  const stringContainer = window.reusableCanvas || (window.reusableCanvas = document.createElement('canvas'))
  const context = stringContainer.getContext('2d')
  context.font = "normal 14px 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI'"

  const canvasMetrics = context.measureText(text)

  return Math.round(canvasMetrics.width + offset)
}

const handleDiscountCellWidth = (channel, pricelistCount) => {
  if (pricelistCount === 1) {
    return 157
  }
  if (pricelistCount === 2) {
    return 90
  }
  return calculateColumnWidthBasedOnText(channel)
}

const filterGeneralDistinct = (value, index, self) => {
  return self.indexOf(value) === index
}

const filterDistinctByProperty = (value, index, self, property) => {
  return self.findIndex(x => x[property] === value[property]) === index
}

const setStickyTableHead = (className, tableRef, isHeadSticky, setStickyCallback) => {
  const headElement = document.querySelector(className)
  const tableShapeRef = tableRef?.current?.getBoundingClientRect()

  if (headElement && tableShapeRef) {
    if (tableShapeRef.top <= 0 && tableShapeRef.bottom > 3 * 35) {
      !isHeadSticky && setStickyCallback(true)
      const calcYPosition = Math.abs(tableShapeRef.top)
      const style = 'translateY(' + calcYPosition + 'px)'
      headElement.style.transform = style
    } else {
      isHeadSticky && setStickyCallback(false)
      const style = 'translateY(' + 0 + 'px)'
      headElement.style.transform = style
    }
  }
}

const getHtmlParentElement = (triggerNode: HTMLElement) => {
  return triggerNode?.parentElement
}

const getHtmlParentForDropdown = (triggerNode: HTMLElement) => {
  return triggerNode?.parentElement?.parentElement
}

const getHtmlRowParentForPopover = (triggerNode: HTMLElement) => {
  return triggerNode?.parentElement?.parentElement?.parentElement
}

const getHtmlTableAutocompleteParentElement = (triggerNode: HTMLElement) => {
  return triggerNode?.parentElement?.parentElement?.parentElement?.parentElement?.parentElement
}

const handleModalTriggerEvent = (
  event,
  bodyRef,
  changesToSave,
  showModalCallback,
  noChangesCallback = null,
  extraRef = null,
) => {
  const extraBodyCheck = !extraRef || (extraRef && !extraRef.contains(event?.target))

  if (bodyRef && !bodyRef.contains(event?.target) && extraBodyCheck) {
    var selection = window.getSelection().toString()

    //prevent if theres text selected to trigger modal
    if (selection) {
      event.stopPropagation()
      event.preventDefault()
      return
    }

    const targetClass = getClassNameFromHtmlElement(event?.target)
    const parentClass = getClassNameFromHtmlElement(event?.target?.parentElement)
    const languageDropdownSelectCheckClass = getClassNameFromHtmlElement(event?.target?.parentElement?.parentElement)
    const languageDropdownOptionsCheckClass = getClassNameFromHtmlElement(
      event?.target?.parentElement?.parentElement?.parentElement?.parentElement?.parentElement?.parentElement
        ?.parentElement,
    )

    const preventThemeSwitchTrigger =
      !parentClass.includes('theme-btn') &&
      !parentClass.includes('icon-lightbulb') &&
      !targetClass.includes('theme-btn') &&
      !targetClass.includes('icon-lightbulb')

    //Check for dropdown sub items that can't be attached to container
    const parentClassNameCheckToPreventTrigger =
      !parentClass.includes('exclude-modal-click') && !targetClass.includes('exclude-modal-click')
    const languageDropdownSelectPreventTrigger =
      !languageDropdownSelectCheckClass.includes('exclude-modal-click') &&
      !languageDropdownSelectCheckClass.includes('language-dropdown')

    const languageDropdownOptionsPreventTrigger = !languageDropdownOptionsCheckClass.includes('exclude-modal-click')

    if (
      changesToSave &&
      preventThemeSwitchTrigger &&
      parentClassNameCheckToPreventTrigger &&
      languageDropdownSelectPreventTrigger &&
      languageDropdownOptionsPreventTrigger
    ) {
      showModalCallback()
    } else {
      if (
        noChangesCallback &&
        preventThemeSwitchTrigger &&
        parentClassNameCheckToPreventTrigger &&
        languageDropdownSelectPreventTrigger &&
        languageDropdownOptionsPreventTrigger
      ) {
        noChangesCallback()
      }
    }
  }
}

const generateWorkspaceName = (workspace: string, userValue: string, config: string) => {
  if (config && !isEncoded(config)) {
    config = encodeURIComponent(config)
  }

  return `${workspace}_${userValue || userValue === '0' ? userValue : 'noProfile'}_${config || 'noConfig'}`
}

const retrieveAntdLocale = currentLanguage => {
  switch (currentLanguage) {
    case Language_EN:
      return enUs
    case Language_ES:
      return esEs
    case Language_FR:
      return frFr
    default:
      return itIt
  }
}

const isEncoded = value => {
  value = value || ''

  return value !== decodeURIComponent(value)
}

const retrieveSizeValuesFromCheckedStore = (purchased, delivered, checkedStores) => {
  checkedStores.forEach(store => {
    retrieveSizeValuesFromIndividualStore(purchased, delivered, store)
  })
}

const retrieveSizeValuesFromIndividualStore = (purchased, delivered, store) => {
  store[Purchased].forEach(rowElement => {
    purchased.push(rowElement)
  })

  store[Delivered].forEach(rowElement => {
    delivered.push(rowElement)
  })
}

const sendMessage = message => {
  if (isFromIntegration(window)) {
    const msg = JSON.stringify(message)

    window.chrome.webview.postMessage(msg)
    console.log(`Inviato all'integrazione: ${msg}`)
  }
}

const getClassNameFromHtmlElement = (htmlElementTarget: any) => {
  //check if class is from and svg element, svg className is an object instead of string and return className
  return !!htmlElementTarget?.className &&
    (htmlElementTarget?.className?.baseVal || htmlElementTarget?.className?.baseVal === '')
    ? htmlElementTarget?.className?.baseVal
    : !!htmlElementTarget?.className || htmlElementTarget?.className === ''
    ? htmlElementTarget?.className
    : ''
}

const getInfoByChannelType = async (typeOfChannel: TypeOfChannel, dispatch, callback?) => {
  let action

  switch (typeOfChannel) {
    case TypeOfChannel.Marketplace:
      action = getChannels()
      break
    case TypeOfChannel.Site:
      action = getSitePricelists()
      break
    case TypeOfChannel.Store:
      action = getStores()
      break
  }

  if (action) {
    try {
      await dispatch(action)

      if (callback) {
        callback()
      }
    } catch (error) {
      console.error('Error dispatching action:', error)
    }
  }
}

export const logProxy = (text: string, proxy: any) => {
  console.log(text, JSON.parse(JSON.stringify(proxy)))
}

export {
  sendMessage,
  isFixedWorkspace,
  fixWorkSpaceCase,
  getBaseUrl,
  isLocalhost,
  isDemoSite,
  isDevelopment,
  isProdSite,
  isEmptyObject,
  hasSizeEnabled,
  getParameterByName,
  renderTranslation,
  renderSelectionCountHtml,
  getCurrencyConversion,
  handleErrors,
  retrieveMktAliasOrName,
  isFromIntegration,
  retrieveSelloutAvarageWeighted,
  retrieveColumnWidth,
  calculateColumnFreeWidth,
  retrieveRenderNewLineColumn,
  createRequest,
  createRequestNoStringifyBody,
  handleMultipleTableScroll,
  handleMultipleTableVirtualScroll,
  calculateColumnWidthBasedOnText,
  handleErrorCatchLog,
  filterGeneralDistinct,
  filterDistinctByProperty,
  stopPropagation,
  preventDefault,
  handleRequest,
  setStickyTableHead,
  getCurrencySymbolByPricelist,
  getHtmlParentElement,
  handleModalTriggerEvent,
  getHtmlRowParentForPopover,
  getHtmlParentForDropdown,
  generateWorkspaceName,
  getHtmlTableAutocompleteParentElement,
  retrieveAntdLocale,
  handleMultipleTableVirtualScrollX,
  handleVirtualScroll,
  isEncoded,
  getInfoByChannelType,
  handleDiscountCellWidth,
  handleMultipleTableHover,
  handleMultipleLeaveTableHover,
}
